import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CycleTitleEditor } from "../components/CycleTitleEditor";
import LiveCycleControls from "../components/LiveCycleControls";
import RegisterLeadModal from "../components/RegisterLeadModal";
import RegisterSpendModal from "../components/RegisterSpendModal";
import SaleModal from "../components/SaleModal";
import CreativesPage from "./CreativesPage";
import {
  BarChart3, DollarSign, TrendingUp, Activity, RefreshCw, Plus, Target,
  AlertTriangle, Pencil, Building2, Film,
} from "lucide-react";

const TABS = [
  { key: "resumo", label: "Resumo", icon: BarChart3 },
  { key: "criativos", label: "Criativos", icon: Film },
  { key: "contas", label: "BM & Contas", icon: Building2 },
  { key: "incidentes", label: "Incidentes", icon: AlertTriangle },
];

const STAGE_LABEL = {
  CAVE_PROFILE: "Caverna",
  LIVE_PRETEST: "Pré-Teste",
  LIVE_TEST: "Teste",
  LIVE_PRESCALE: "Pré-Escala",
  LIVE_SCALE: "Escala",
};

const INCIDENT_TYPES = [
  { key: "ad_account_blocked", label: "Conta de anúncios bloqueada" },
  { key: "bm_blocked", label: "BM bloqueada" },
  { key: "creative_blocked", label: "Criativo bloqueado/reprovado" },
  { key: "payment_issue", label: "Problema de pagamento/anúncio" },
  { key: "other", label: "Outro" },
];

const fmtBRL = (val) =>
  `R$ ${Number(val || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("pt-BR", {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
}) : "—";

function BMAdAccountSection({ bms, adAccounts, compare, onReload }) {
  const [showBMForm, setShowBMForm] = useState(false);
  const [showAAForm, setShowAAForm] = useState(false);
  const [bmName, setBmName] = useState("");
  const [bmMetaId, setBmMetaId] = useState("");
  const [aaName, setAaName] = useState("");
  const [aaBmId, setAaBmId] = useState("");
  const [aaAccountId, setAaAccountId] = useState("");

  const createBM = async () => {
    if (!bmName.trim()) return;
    await api.post("/api/bms", { name: bmName, meta_id: bmMetaId || undefined });
    setBmName(""); setBmMetaId(""); setShowBMForm(false);
    onReload?.();
  };
  const createAA = async () => {
    if (!aaName.trim()) return;
    await api.post("/api/ad-accounts", {
      name: aaName, bm_id: aaBmId || undefined, account_id: aaAccountId || undefined,
    });
    setAaName(""); setAaBmId(""); setAaAccountId(""); setShowAAForm(false);
    onReload?.();
  };

  return (
    <div data-testid="bm-adaccount-section" className="bg-[#14161C] border border-[#282D3D] rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <Building2 className="w-4 h-4 text-[#FF6B00]" />
          BM & Contas de Anúncios
        </h3>
        <div className="flex gap-2">
          <button
            data-testid="create-bm-btn"
            onClick={() => setShowBMForm(true)}
            className="text-[11px] font-bold px-2 py-1 rounded bg-[#1F2330] border border-[#3E4760] text-white"
          >
            + BM
          </button>
          <button
            data-testid="create-adaccount-btn"
            onClick={() => setShowAAForm(true)}
            className="text-[11px] font-bold px-2 py-1 rounded bg-[#1F2330] border border-[#3E4760] text-white"
          >
            + Conta
          </button>
        </div>
      </div>

      {showBMForm && (
        <div className="bg-[#1A1D26] border border-[#282D3D] rounded-lg p-3 space-y-2">
          <input data-testid="bm-name-input" value={bmName} onChange={(e)=>setBmName(e.target.value)}
                 placeholder="Nome da BM" className="w-full bg-[#0F1218] border border-[#282D3D] rounded px-2 py-1.5 text-white text-xs" />
          <input data-testid="bm-metaid-input" value={bmMetaId} onChange={(e)=>setBmMetaId(e.target.value)}
                 placeholder="Meta ID (opcional)" className="w-full bg-[#0F1218] border border-[#282D3D] rounded px-2 py-1.5 text-white text-xs" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowBMForm(false)} className="text-[11px] px-2 py-1 rounded border border-[#3E4760] text-gray-300">Cancelar</button>
            <button data-testid="submit-bm-btn" onClick={createBM} className="text-[11px] px-2 py-1 rounded bg-[#FF6B00] text-black font-bold">Criar BM</button>
          </div>
        </div>
      )}

      {showAAForm && (
        <div className="bg-[#1A1D26] border border-[#282D3D] rounded-lg p-3 space-y-2">
          <input data-testid="aa-name-input" value={aaName} onChange={(e)=>setAaName(e.target.value)}
                 placeholder="Nome da conta" className="w-full bg-[#0F1218] border border-[#282D3D] rounded px-2 py-1.5 text-white text-xs" />
          <select data-testid="aa-bm-select" value={aaBmId} onChange={(e)=>setAaBmId(e.target.value)}
                  className="w-full bg-[#0F1218] border border-[#282D3D] rounded px-2 py-1.5 text-white text-xs">
            <option value="">— BM (opcional) —</option>
            {bms.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <input data-testid="aa-accountid-input" value={aaAccountId} onChange={(e)=>setAaAccountId(e.target.value)}
                 placeholder="Account ID (opcional)" className="w-full bg-[#0F1218] border border-[#282D3D] rounded px-2 py-1.5 text-white text-xs" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowAAForm(false)} className="text-[11px] px-2 py-1 rounded border border-[#3E4760] text-gray-300">Cancelar</button>
            <button data-testid="submit-aa-btn" onClick={createAA} className="text-[11px] px-2 py-1 rounded bg-[#FF6B00] text-black font-bold">Criar conta</button>
          </div>
        </div>
      )}

      {compare.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="text-gray-400">
              <tr>
                <th className="text-left py-1 px-1">BM</th>
                <th className="text-left py-1 px-1">Conta</th>
                <th className="text-right py-1 px-1">Gasto</th>
                <th className="text-right py-1 px-1">Leads</th>
                <th className="text-right py-1 px-1">Valores</th>
                <th className="text-right py-1 px-1">Vendas</th>
                <th className="text-right py-1 px-1">Receita</th>
                <th className="text-right py-1 px-1">ROI</th>
                <th className="text-right py-1 px-1">Ações</th>
              </tr>
            </thead>
            <tbody>
              {compare.map((a) => (
                <tr key={a.id} data-testid={`aa-row-${a.id}`} className="border-t border-[#282D3D] text-gray-200">
                  <td className="py-1 px-1 text-gray-300">{a.bm_name || "—"}</td>
                  <td className="py-1 px-1 font-bold">{a.name}</td>
                  <td className="text-right">{fmtBRL(a.total_spend)}</td>
                  <td className="text-right">{a.total_leads}</td>
                  <td className="text-right">{a.total_reached_values}</td>
                  <td className="text-right">{a.total_sales}</td>
                  <td className="text-right text-emerald-400">{fmtBRL(a.total_revenue)}</td>
                  <td className="text-right text-[#FF6B00]">{a.roi?.toFixed?.(2) || "0.00"}x</td>
                  <td className="text-right">
                    <div className="inline-flex gap-1">
                      <button
                        data-testid={`aa-edit-${a.id}`}
                        onClick={() => {
                          const newName = window.prompt("Novo nome da conta:", a.name);
                          if (newName && newName !== a.name) {
                            api.patch(`/api/ad-accounts/${a.id}`, { name: newName }).then(onReload);
                          }
                        }}
                        className="px-1.5 py-0.5 text-[10px] rounded bg-[#1A1D26] border border-[#3E4760] text-gray-300 hover:border-[#FF6B00]"
                        title="Editar"
                      >
                        Editar
                      </button>
                      <button
                        data-testid={`aa-reset-${a.id}`}
                        onClick={async () => {
                          if (!window.confirm(`Zerar os dados do ciclo atual da conta "${a.name}"? O histórico de ciclos anteriores é preservado.`)) return;
                          await api.post(`/api/ad-accounts/${a.id}/reset-current`, {});
                          onReload?.();
                        }}
                        className="px-1.5 py-0.5 text-[10px] rounded bg-amber-500/15 border border-amber-500/40 text-amber-300 hover:bg-amber-500/25"
                        title="Zerar dados do ciclo atual"
                      >
                        Zerar
                      </button>
                      <button
                        data-testid={`aa-delete-${a.id}`}
                        onClick={async () => {
                          if (!window.confirm(`Excluir a conta "${a.name}"? O histórico de atribuição é preservado.`)) return;
                          await api.delete(`/api/ad-accounts/${a.id}`);
                          onReload?.();
                        }}
                        className="px-1.5 py-0.5 text-[10px] rounded bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25"
                        title="Excluir conta"
                      >
                        Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adAccounts.length === 0 && !showAAForm && (
        <div className="text-[11px] text-gray-500">Nenhuma conta de anúncios cadastrada ainda.</div>
      )}
    </div>
  );
}

function IncidentsSection({ incidents, bms, adAccounts, creatives, onReload }) {
  const [showForm, setShowForm] = useState(false);
  const [type, setType] = useState("ad_account_blocked");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [bmId, setBmId] = useState("");
  const [aaId, setAaId] = useState("");
  const [creativeId, setCreativeId] = useState("");

  const submit = async () => {
    if (!title.trim()) return;
    await api.post("/api/incidents", {
      incident_type: type,
      title,
      description: description || undefined,
      related_bm_id: bmId || undefined,
      related_ad_account_id: aaId || undefined,
      related_creative_id: creativeId || undefined,
    });
    setShowForm(false); setTitle(""); setDescription("");
    setBmId(""); setAaId(""); setCreativeId("");
    onReload?.();
  };

  const resolve = async (id) => {
    await api.post(`/api/incidents/${id}/resolve`, { notes: "" });
    onReload?.();
  };

  return (
    <div data-testid="incidents-section" className="bg-[#14161C] border border-[#282D3D] rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-400" />
          Problemas operacionais ({incidents.length} abertos)
        </h3>
        <button
          data-testid="create-incident-btn"
          onClick={() => setShowForm(true)}
          className="text-[11px] font-bold px-2 py-1 rounded bg-[#1F2330] border border-[#3E4760] text-white"
        >
          Registrar problema
        </button>
      </div>

      {showForm && (
        <div className="bg-[#1A1D26] border border-[#282D3D] rounded-lg p-3 space-y-2">
          <select data-testid="incident-type-select" value={type} onChange={(e)=>setType(e.target.value)}
                  className="w-full bg-[#0F1218] border border-[#282D3D] rounded px-2 py-1.5 text-white text-xs">
            {INCIDENT_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <input data-testid="incident-title-input" value={title} onChange={(e)=>setTitle(e.target.value)}
                 placeholder="Título" className="w-full bg-[#0F1218] border border-[#282D3D] rounded px-2 py-1.5 text-white text-xs" />
          <textarea data-testid="incident-description-input" value={description} onChange={(e)=>setDescription(e.target.value)}
                    placeholder="Descrição (opcional)" rows={2}
                    className="w-full bg-[#0F1218] border border-[#282D3D] rounded px-2 py-1.5 text-white text-xs" />
          <div className="grid grid-cols-3 gap-2">
            <select value={bmId} onChange={(e)=>setBmId(e.target.value)}
                    className="bg-[#0F1218] border border-[#282D3D] rounded px-1 py-1 text-white text-[11px]">
              <option value="">BM</option>
              {bms.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <select value={aaId} onChange={(e)=>setAaId(e.target.value)}
                    className="bg-[#0F1218] border border-[#282D3D] rounded px-1 py-1 text-white text-[11px]">
              <option value="">Conta</option>
              {adAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <select value={creativeId} onChange={(e)=>setCreativeId(e.target.value)}
                    className="bg-[#0F1218] border border-[#282D3D] rounded px-1 py-1 text-white text-[11px]">
              <option value="">Criativo</option>
              {creatives.map(c => <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="text-[11px] px-2 py-1 rounded border border-[#3E4760] text-gray-300">Cancelar</button>
            <button data-testid="submit-incident-btn" onClick={submit} className="text-[11px] px-2 py-1 rounded bg-[#FF6B00] text-black font-bold">Registrar</button>
          </div>
        </div>
      )}

      {incidents.length === 0 ? (
        <div className="text-[11px] text-gray-500">Sem problemas abertos.</div>
      ) : incidents.map(i => (
        <div key={i.id} data-testid={`incident-row-${i.id}`} className="bg-[#1A1D26] border border-[#282D3D] rounded-lg p-2 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-white text-xs font-bold">{i.title}</div>
            <div className="text-[10px] text-gray-400">{INCIDENT_TYPES.find(t=>t.key===i.incident_type)?.label} · {fmtDate(i.started_at)}</div>
            {i.description && <div className="text-[11px] text-gray-300 mt-1">{i.description}</div>}
          </div>
          <button
            data-testid={`resolve-incident-${i.id}`}
            onClick={() => resolve(i.id)}
            className="text-[11px] font-bold px-2 py-1 rounded bg-emerald-600/20 text-emerald-300 border border-emerald-600/40 shrink-0"
          >
            Marcar como resolvido
          </button>
        </div>
      ))}
    </div>
  );
}

export default function OperationPage() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [sales, setSales] = useState([]);
  const [bms, setBms] = useState([]);
  const [adAccounts, setAdAccounts] = useState([]);
  const [compare, setCompare] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showLead, setShowLead] = useState(false);
  const [showSale, setShowSale] = useState(false);
  const [showSpend, setShowSpend] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [reachedValuesBusy, setReachedValuesBusy] = useState(false);

  const activeTab = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("tab");
    return TABS.some((x) => x.key === t) ? t : "resumo";
  }, [location.search]);

  const setTab = useCallback(
    (tab) => {
      const params = new URLSearchParams(location.search);
      if (tab === "resumo") params.delete("tab");
      else params.set("tab", tab);
      navigate(
        { pathname: "/operacao", search: params.toString() ? `?${params.toString()}` : "" },
        { replace: false }
      );
    },
    [location.search, navigate]
  );

  const refresh = useCallback(async () => {
    try {
      const [dash, salesRes, bmsRes, aaRes, cmpRes, incRes] = await Promise.all([
        api.get("/api/operations/dashboard"),
        api.get("/api/sales/?active_cycles_only=true"),
        api.get("/api/bms"),
        api.get("/api/ad-accounts"),
        api.get("/api/ad-accounts/compare"),
        api.get("/api/incidents?status=open"),
      ]);
      setData(dash.data);
      setSales(salesRes.data?.sales || []);
      setBms(bmsRes.data?.bms || []);
      setAdAccounts(aaRes.data?.ad_accounts || []);
      setCompare(cmpRes.data?.ad_accounts || []);
      setIncidents(incRes.data?.incidents || []);
    } catch (err) {
      console.error("op refresh error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user) refresh(); }, [user, refresh]);

  const quickReachedValues = async () => {
    // Shortcut button: register a lead + mark as reached_values in one click.
    setShowLead(true);
  };

  if (loading && !data) {
    return <div data-testid="operation-page-container" className="p-8 text-center text-gray-400">Carregando painel de operação...</div>;
  }

  const op = data?.operation || {};
  const campaign = data?.campaign;
  const timers = data?.timers || {};
  const timeline = data?.timeline || [];
  const activeCycle = data?.active_cycle;
  const stage = op?.stage || "CAVE_PROFILE";
  const stageLabel = STAGE_LABEL[stage] || stage;

  // Resumo reflects ONLY active Live cycles — closing a cycle resets to zero.
  const totalSpend = op?.current_spend || 0;
  const totalRev = op?.current_revenue || 0;
  const roi = totalSpend > 0 ? (totalRev / totalSpend).toFixed(2) : "0.00";
  const totalLeads = op?.current_leads || 0;
  const totalReached = op?.current_reached_values || 0;

  return (
    <div data-testid="operation-page-container" className="space-y-6 max-w-5xl mx-auto px-4 py-4 pb-20">
      <LiveCycleControls onChange={refresh} version={data} />

      {/* Header */}
      <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs font-bold text-[#FF6B00] uppercase tracking-wider mb-1">
            <BarChart3 className="w-4 h-4" />
            <span>Painel Operacional</span>
          </div>
          <CycleTitleEditor cycle={activeCycle} fallback={campaign?.name || "Operação Modo Caverna"} onSaved={refresh} />
          <span data-testid="current-stage-label" className="text-xs text-gray-400">
            Fase atual: <strong className="text-white">{stageLabel}</strong>
            {activeCycle && (
              <span className="ml-2 text-[11px]">
                · Ciclo #{activeCycle.cycle_number}
              </span>
            )}
          </span>
        </div>
        <button onClick={refresh}
                className="p-2 bg-[#1E2330] hover:bg-[#282F42] border border-[#282D3D] rounded-lg text-gray-300 text-xs flex items-center gap-1.5 self-start sm:self-auto">
          <RefreshCw className="w-3.5 h-3.5" /><span>Atualizar</span>
        </button>
      </div>

      {/* Sub-tabs — pill style */}
      <div
        data-testid="operation-subtabs"
        role="tablist"
        aria-label="Operação"
        className="flex flex-wrap items-center gap-2 bg-[#14161C] border border-[#282D3D] rounded-xl p-1.5"
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-testid={`op-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider transition-all ${
                isActive
                  ? "bg-[#FF6B00] text-black shadow-[0_0_12px_rgba(255,107,0,0.35)]"
                  : "text-gray-300 hover:text-white hover:bg-[#1F2330]"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {activeTab === "resumo" && (
        <>
          {/* Live quick-actions — only when a cycle is active */}
          {activeCycle ? (
            <div data-testid="live-actions" className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <button
                data-testid="action-lead"
                onClick={() => setShowLead(true)}
                className="py-3 rounded-xl bg-[#14161C] border border-[#282D3D] hover:border-[#FF6B00] text-white font-extrabold text-xs uppercase flex flex-col items-center gap-1"
              >
                <Plus className="w-5 h-5 text-[#FF6B00]" />
                + Lead
              </button>
              <button
                data-testid="action-reached-values"
                onClick={quickReachedValues}
                disabled={reachedValuesBusy}
                className="py-3 rounded-xl bg-[#14161C] border border-[#282D3D] hover:border-[#FF6B00] text-white font-extrabold text-xs uppercase flex flex-col items-center gap-1 disabled:opacity-60"
              >
                <Target className="w-5 h-5 text-[#FF6B00]" />
                + Chegou em Valores
              </button>
              <button
                data-testid="action-spend"
                onClick={() => setShowSpend(true)}
                className="py-3 rounded-xl bg-[#14161C] border border-[#282D3D] hover:border-amber-400 text-white font-extrabold text-xs uppercase flex flex-col items-center gap-1"
              >
                <DollarSign className="w-5 h-5 text-amber-400" />
                + Gasto
              </button>
              <button
                data-testid="action-sale"
                onClick={() => { setEditingSale(null); setShowSale(true); }}
                className="py-3 rounded-xl bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase flex flex-col items-center gap-1"
              >
                <DollarSign className="w-5 h-5" />
                + Venda
              </button>
            </div>
          ) : (
            <div data-testid="no-cycle-cta" className="bg-yellow-500/10 border border-yellow-500/40 rounded-xl p-4 text-center text-yellow-200 text-sm">
              Nenhum ciclo Live ativo. Clique em <strong>Iniciar novo ciclo</strong> para registrar leads e vendas.
            </div>
          )}

          {/* Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4">
              <span className="text-xs text-gray-400 font-semibold block mb-1">Investimento do Ciclo</span>
              <span data-testid="metric-spend" className="text-lg sm:text-xl font-extrabold text-white block">{fmtBRL(totalSpend)}</span>
            </div>
            <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4">
              <span className="text-xs text-gray-400 font-semibold block mb-1">Receita</span>
              <span data-testid="metric-revenue" className="text-lg sm:text-xl font-extrabold text-emerald-400 block">{fmtBRL(totalRev)}</span>
            </div>
            <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4">
              <span className="text-xs text-gray-400 font-semibold block mb-1">ROI</span>
              <span data-testid="metric-roi" className="text-lg sm:text-xl font-extrabold text-[#FF6B00] block">{roi}x</span>
            </div>
            <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4">
              <span className="text-xs text-gray-400 font-semibold block mb-1">Vendas</span>
              <span data-testid="metric-sales-count" className="text-lg sm:text-xl font-extrabold text-white block">{op?.current_sales || 0}</span>
            </div>
          </div>

          {/* Leads totals (replaces old funnel) */}
          <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-5">
            <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#FF6B00]" />
              Leads do ciclo
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#1A1D26] border border-[#282D3D] rounded-lg p-3">
                <div className="text-[11px] text-gray-400">Leads entraram</div>
                <div data-testid="leads-entered-count" className="text-xl font-extrabold text-white">{totalLeads}</div>
              </div>
              <div className="bg-[#1A1D26] border border-[#282D3D] rounded-lg p-3">
                <div className="text-[11px] text-gray-400">Chegaram em Valores</div>
                <div data-testid="leads-reached-count" className="text-xl font-extrabold text-[#FF6B00]">{totalReached}</div>
              </div>
            </div>
          </div>

          {/* Sales history */}
          <div data-testid="sales-list-section" className="bg-[#14161C] border border-[#282D3D] rounded-xl p-5">
            <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-400" />
              Vendas do ciclo ({sales.length})
            </h3>
            {sales.length === 0 && (
              <div className="text-[11px] text-gray-500">Nenhuma venda registrada neste ciclo.</div>
            )}
            <div className="space-y-2">
              {sales.map((s) => (
                <div key={s.id || s._id}
                     data-testid={`sale-row-${s.id || s._id}`}
                     className="bg-[#1A1D26] border border-[#282D3D] rounded-lg p-2 flex items-center justify-between gap-2 text-xs">
                  <div className="min-w-0">
                    <div className="text-white font-bold">
                      {fmtBRL(s.value)}{s.quantity > 1 && <span className="text-gray-400"> × {s.quantity}</span>}
                    </div>
                    <div className="text-[10px] text-gray-400">
                      {fmtDate(s.occurred_at || s.created_at)}
                      {s.creative_id && <span> · criativo <code>{s.creative_id.slice(-6)}</code></span>}
                    </div>
                  </div>
                  <button
                    data-testid={`edit-sale-${s.id || s._id}`}
                    onClick={() => { setEditingSale({ ...s, id: s.id || s._id }); setShowSale(true); }}
                    className="text-[11px] font-bold px-2 py-1 rounded bg-[#1F2330] border border-[#3E4760] text-white hover:text-[#FF6B00] flex items-center gap-1"
                  >
                    <Pencil className="w-3 h-3" /> Editar venda
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-5">
            <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#FF6B00]" />
              Linha do Tempo
            </h3>
            {timeline.length > 0 ? (
              <div className="space-y-2">
                {timeline.map((evt, idx) => (
                  <div key={evt._id || evt.id || idx}
                       className="bg-[#1A1D26] border border-[#282D3D] rounded-lg p-2.5 text-xs">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="font-bold text-white">{evt.title}</span>
                      <span className="text-[10px] text-gray-400 font-mono">{fmtDate(evt.created_at)}</span>
                    </div>
                    <p className="text-gray-300">{evt.description}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-gray-400 text-center py-6">Nenhum evento registrado.</div>
            )}
          </div>
        </>
      )}

      {activeTab === "criativos" && (
        <div data-testid="op-tab-content-criativos">
          <CreativesPage />
        </div>
      )}

      {activeTab === "contas" && (
        <div data-testid="op-tab-content-contas">
          <BMAdAccountSection bms={bms} adAccounts={adAccounts} compare={compare} onReload={refresh} />
        </div>
      )}

      {activeTab === "incidentes" && (
        <div data-testid="op-tab-content-incidentes">
          <IncidentsSection
            incidents={incidents}
            bms={bms}
            adAccounts={adAccounts}
            creatives={(data?.operation?.creatives) || []}
            onReload={refresh}
          />
        </div>
      )}

      <RegisterLeadModal
        isOpen={showLead}
        onClose={() => setShowLead(false)}
        onLeadSuccess={refresh}
      />
      <RegisterSpendModal
        isOpen={showSpend}
        onClose={() => setShowSpend(false)}
        onSuccess={refresh}
      />
      <SaleModal
        isOpen={showSale}
        onClose={() => { setShowSale(false); setEditingSale(null); }}
        onSaved={refresh}
        sale={editingSale}
      />
    </div>
  );
}
